// src/pages/About.jsx
import React from 'react';
import { Mail, Linkedin, ArrowLeft, Sparkles, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import DarkModeToggle from '@components/DarkModeToggle';

export default function About() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analyzer
          </Link>
          <DarkModeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 md:p-12">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">About SmartResumeTool</h1>
            <p className="text-gray-600 dark:text-gray-400">AI-powered resume optimization made simple</p>
          </div>

          {/* Why I Built This Section */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Why I Built This</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Job searching is challenging enough without worrying about whether your resume will make it past 
              an ATS system. I built SmartResumeTool to help job seekers optimize their resumes using AI, 
              making the application process more accessible and successful for everyone.
            </p>
            <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-6 italic text-gray-700 dark:text-gray-300 text-center">
              "Technology should solve real problems and be simple to use"
            </div>
          </div>

          {/* Key Features */}
          <div className="mb-10">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">What This Tool Does</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">AI Analysis</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Powered by Claude AI to provide intelligent, context-aware resume feedback
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Keyword Matching</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Identifies missing keywords to help you pass ATS screening
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Instant Results</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Get actionable feedback in seconds, not hours
                </p>
              </div>
            </div>
          </div>

          {/* About Creator - Simplified */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-8 mb-10 border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">About the Creator</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-1">David Madison</p>
              <p className="text-sm text-gray-500 dark:text-gray-500">Technology Professional & AI Enthusiast</p>
            </div>
            
            {/* Background */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Background</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                Data Analyst with expertise in financial forecasting, business intelligence, and machine learning. 
                U.S. Army veteran combining technical proficiency with analytical rigor.
              </p>
            </div>

            {/* Education */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Education</h4>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <p>• M.S. in Data Analytics - Southern New Hampshire University</p>
                <p>• B.S. in Information Technology - American Military University (3.85 GPA)</p>
              </div>
            </div>

            {/* Technical Skills */}
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Technical Skills</h4>
              <div className="flex flex-wrap gap-2">
                {[
                  'Python',
                  'JavaScript',
                  'React.js',
                  'SQL',
                  'Machine Learning',
                  'Tableau',
                  'Power BI'
                ].map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg text-xs font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Contact Links */}
            <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <a
                href="mailto:davidmadison95@yahoo.com"
                className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium text-sm"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
              <a
                href="https://www.linkedin.com/in/davidmadison95/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium text-sm"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </a>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-gray-600 dark:text-gray-400 mb-4">Ready to optimize your resume?</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
            >
              Try SmartResumeTool Now
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}